/**
 * Campaign engine — which time-boxed offers are live right now.
 *
 * Campaigns are separate from coupons: a campaign is a property of the calendar,
 * a coupon is something a buyer types. A campaign with `autoApply: true` prices
 * every order in its window without the buyer doing anything.
 */

import { CAMPAIGNS } from "./pricing-config";
import type { DiscountRule, RegionCode } from "./pricing-types";
import { withinWindow } from "./pricing-utils";

/** Every campaign live at `now` for `region`, regardless of autoApply. */
export function activeCampaigns(now: Date, region: RegionCode): DiscountRule[] {
  return CAMPAIGNS.filter((c) => {
    if (c.active === false) return false;
    if (c.regions && c.regions.length > 0 && !c.regions.includes(region)) return false;
    return withinWindow(now, c.validFrom, c.validUntil).ok;
  });
}

/** Campaigns that price an order on their own, with no code typed. */
export function autoApplyCampaigns(now: Date, region: RegionCode): DiscountRule[] {
  return activeCampaigns(now, region).filter((c) => c.autoApply === true);
}

export function findCampaign(id: string): DiscountRule | null {
  return CAMPAIGNS.find((c) => c.id === id) ?? null;
}

/**
 * A campaign explicitly requested by the caller. Returns null when it is not
 * live, so a stale link can never resurrect an expired price.
 */
export function resolveCampaign(
  id: string | null | undefined,
  now: Date,
  region: RegionCode,
): DiscountRule | null {
  if (!id) return null;
  return activeCampaigns(now, region).find((c) => c.id === id) ?? null;
}
